/*
 * Cankaya Collection — carrousel des cartes produit sur écran tactile (26/09/2026).
 * Horizon permet déjà de faire glisser les photos d'une carte au doigt, mais rien ne le signale.
 * Ce script ajoute, uniquement sur écran tactile :
 *   1. des points indiquant le nombre de photos et celle affichée ;
 *   2. un aperçu unique par session : sur la 1re carte bien visible, la photo glisse un instant
 *      pour laisser voir la suivante, puis revient (animation graphique, le défilement n'est pas touché).
 * Aucun effet sur ordinateur : le survol à la souris d'Horizon reste inchangé.
 * Pour annuler : retirer les 2 lignes cc-carrousel dans snippets/stylesheets.liquid.
 */
(() => {
  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;

  const MAX_DOTS = 5;
  const SESSION_KEY = 'cc-carrousel-apercu';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const galleries = new WeakSet();

  const visibleSlides = (slideshow) =>
    [...slideshow.querySelectorAll('slideshow-slide')].filter((s) => !s.hidden || s.hasAttribute('reveal'));

  const alreadyPeeked = () => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const markPeeked = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      /* stockage indisponible : l'aperçu pourra se rejouer, sans conséquence */
    }
  };

  function setup(gallery) {
    if (galleries.has(gallery)) return;
    const slideshow = gallery.querySelector('slideshow-component');
    const scroller = slideshow && slideshow.querySelector('slideshow-slides');
    if (!scroller) return;
    // Carte placée dans un carrousel de produits : Horizon y désactive le glissement, donc pas de points.
    if (slideshow.parentElement && slideshow.parentElement.closest('slideshow-component')) return;
    galleries.add(gallery);

    const dots = document.createElement('div');
    dots.className = 'cc-dots';
    dots.setAttribute('aria-hidden', 'true');
    gallery.appendChild(dots);

    // Place les points juste au-dessus du bouton « Choisir » s'il est présent.
    const quickAdd = gallery.querySelector('.quick-add__button');
    if (quickAdd) {
      const g = gallery.getBoundingClientRect();
      const b = quickAdd.getBoundingClientRect();
      if (b.height > 0 && b.top > g.top) gallery.style.setProperty('--cc-dots-bottom', `${Math.round(g.bottom - b.top + 8)}px`);
    }

    let count = 0;
    let active = -1;
    const update = () => {
      if (count < 2) return;
      const w = scroller.clientWidth || 1;
      const index = Math.min(Math.max(Math.round(scroller.scrollLeft / w), 0), count - 1);
      if (index === active) return;
      active = index;
      [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === index));
    };
    const render = () => {
      count = Math.min(visibleSlides(slideshow).length, MAX_DOTS);
      dots.hidden = count < 2 || slideshow.getAttribute('disabled') === 'true';
      if (dots.childElementCount !== count) {
        dots.replaceChildren(...Array.from({ length: count }, () => document.createElement('span')));
        active = -1;
      }
      update();
    };

    scroller.addEventListener('scroll', update, { passive: true });
    scroller.addEventListener('scrollend', update, { passive: true });
    // Le choix d'un coloris masque/affiche des photos : on recompte.
    new MutationObserver(render).observe(slideshow, { subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
    render();
    if (count > 1 && peekObserver) peekObserver.observe(gallery);
  }

  // Aperçu unique par session, sur la 1re carte visible à 90 %.
  const peekObserver =
    alreadyPeeked() || reduceMotion.matches
      ? null
      : new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting || entry.intersectionRatio < 0.9) continue;
              peekObserver.disconnect();
              if (alreadyPeeked()) return;
              markPeeked();
              setTimeout(() => peek(entry.target), 700);
              return;
            }
          },
          { threshold: [0.9] }
        );

  async function peek(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    const scroller = slideshow && slideshow.querySelector('slideshow-slides');
    if (!scroller || scroller.scrollLeft > 2 || slideshow.getAttribute('disabled') === 'true') return;
    const [first, next] = visibleSlides(slideshow);
    if (!first || !next) return;

    // S'assurer que la photo suivante est chargée et affichable avant de la montrer.
    next.style.contentVisibility = 'visible';
    const img = next.querySelector('img');
    if (img) {
      img.loading = 'eager';
      try {
        await Promise.race([img.decode(), new Promise((r) => setTimeout(r, 1500))]);
      } catch (e) {
        /* image non décodable : l'aperçu montrera le fond, sans gravité */
      }
    }
    if (scroller.scrollLeft > 2) {
      next.style.contentVisibility = '';
      return;
    }

    const keyframes = [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-28%)', offset: 0.4 },
      { transform: 'translateX(-28%)', offset: 0.6 },
      { transform: 'translateX(0)' },
    ];
    const animations = [first, next].map((s) => s.animate(keyframes, { duration: 1600, easing: 'ease-in-out' }));
    // Si la cliente touche la photo pendant l'aperçu, on lui rend la main aussitôt.
    const cancel = () => animations.forEach((a) => a.cancel());
    scroller.addEventListener('touchstart', cancel, { once: true, passive: true });
    try {
      await animations[0].finished;
    } catch (e) {
      /* animation annulée par un toucher */
    }
    scroller.removeEventListener('touchstart', cancel);
    next.style.contentVisibility = '';
  }

  const scan = () => document.querySelectorAll('.card-gallery').forEach(setup);

  const start = () => {
    scan();
    // Cartes ajoutées plus tard (filtres, pagination, recommandations).
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(() => {
        queued = false;
        scan();
      }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
